import { useStore } from '../store/mst';
import ComponentNames from './constants/componentNames';
import UserPermissions from './constants/userPermissions';
import Conditions from './constants/conditions';
import DriversComponentRegistry from './featuresRegistry/driversComponentRegistry';
import RoutesComponentRegistry from './featuresRegistry/routesComponentRegistry';
import OrdersComponentRegistry from './featuresRegistry/ordersComponentRegistry';
import CompanySettingsComponentRegistry from './featuresRegistry/companySettingsComponentRegistry';
import RouterComponentRegistry from './featuresRegistry/routerComponentRegistry';
import WorkspacesComponentRegistry from './featuresRegistry/workspacesComponentRegistry';
import SchedulingComponentRegistry from './featuresRegistry/schedulingComponentRegistry';
import { snakeCaseToCamelCase } from './utils';
import DebugComponentRegistry from './featuresRegistry/debugComponentRegistry';
import BillingComponentRegistry from './featuresRegistry/billingComponentRegistry';

type OverridingConditions = {
  [name in Conditions]: boolean | undefined | null;
};

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

type TransformEnumValue<S extends string> =
  S extends `${infer First}_${infer Rest}`
    ? `${Capitalize<Lowercase<First>>}${TransformEnumValue<Rest>}`
    : Capitalize<Lowercase<S>>;

export type PermissionResults = {
  [name in UserPermissions as `can${TransformEnumValue<name>}`]?: boolean | undefined;
};

export type PermissionRules = PartialRecord<UserPermissions, boolean | undefined | Function>;

export const enum Features {
  Billing = 'Billing',
  Debug = 'Debug',
  Drivers = 'Drivers',
  Orders = 'Orders',
  CompanySettings = 'CompanySettings',
  Router = 'Router',
  Routes = 'Routes',
  Scheduling = 'Scheduling',
  Workspaces = 'Workspaces',
}

const FeatureComponentRegistry = {
  Billing: BillingComponentRegistry,
  Debug: DebugComponentRegistry,
  Drivers: DriversComponentRegistry,
  Orders: OrdersComponentRegistry,
  CompanySettings: CompanySettingsComponentRegistry,
  Router: RouterComponentRegistry,
  Routes: RoutesComponentRegistry,
  Scheduling: SchedulingComponentRegistry,
  Workspaces: WorkspacesComponentRegistry,
};

const getComponentRegistry = (feature: Features, componentName: ComponentNames) => {
  const featureRegistry: any = FeatureComponentRegistry[feature];

  if (!featureRegistry) {
    throw new Error(`Feature registry for ${feature} not found.`);
  }

  const componentDetails = featureRegistry[componentName];
  if (!componentDetails) {
    throw new Error(`Component permissions for ${componentName} not found.`);
  }
  return componentDetails as PermissionRules;
};

const usePermissions = (feature: Features, componentName: ComponentNames, overrideConditions?: PartialRecord<Conditions, boolean | undefined | null>): PermissionResults => {
  const store = useStore();
  const { account, ui } = store;
  const { selectedWorkspace, selectedRoute } = ui;

  const defaultConditions: OverridingConditions = {
    isSynced: selectedWorkspace?.isSynced,
    workspaceDeleted: selectedWorkspace?.deleted,
    noWorkspaces: !account.user?.projectsCount,
    isDispatched: selectedRoute?.isDispatched,
    isStarted: selectedRoute?.isStarted,
    isFinished: selectedRoute?.finished,
    isLocked: selectedRoute?.isLocked,
    isPublishing: ui.isPublishing,
    isOptimizing: ui.isOptimizing,
  };

  const conditions: OverridingConditions = { ...defaultConditions, ...overrideConditions };

  if (!account.user || !account.user?.permissions) return {};

  const componentPermissions = getComponentRegistry(feature, componentName);

  const result: any = {};

  Object.keys(componentPermissions).forEach((permission) => {
    const permissionValue = componentPermissions[permission as UserPermissions];

    const allowed = typeof permissionValue === 'function'
      ? permissionValue(conditions)
      : permissionValue;

    result[snakeCaseToCamelCase(permission)] = allowed && account.hasPermission(permission as UserPermissions);
  });

  return result;
};

export default usePermissions;
